import { z } from 'zod'
import type { Language, SimulationPublic } from './schemas/simulation'

export const INGREDIENT_LOCK_FIELDS = ['name', 'qty', 'unit'] as const
export const ingredientLocksSchema = z
  .array(
    z
      .object({
        ingredientId: z.string().min(1).max(60),
        fields: z
          .array(z.enum(INGREDIENT_LOCK_FIELDS))
          .min(1)
          .max(3)
          .refine((fields) => new Set(fields).size === fields.length),
      })
      .strict(),
  )
  .max(25)
  .refine((locks) => new Set(locks.map((lock) => lock.ingredientId)).size === locks.length)
export type IngredientLock = z.infer<typeof ingredientLocksSchema>[number]

export class IngredientLockError extends Error {
  readonly code: 'INGREDIENT_LOCK_INVALID' | 'INGREDIENT_LOCK_CONFLICT'
  constructor(
    code: 'INGREDIENT_LOCK_INVALID' | 'INGREDIENT_LOCK_CONFLICT' = 'INGREDIENT_LOCK_CONFLICT',
  ) {
    super(code)
    this.name = 'IngredientLockError'
    this.code = code
  }
}

/** IDs are existing row anchors, never model-generated JSON pointers or array positions. */
export function validIngredientLocks(
  base: SimulationPublic,
  locks: readonly IngredientLock[],
): boolean {
  if (!ingredientLocksSchema.safeParse(locks).success) return false
  if (!Array.isArray(base?.ingredients)) return locks.length === 0
  return locks.every(
    (lock) =>
      base.ingredients.filter((ingredient) => ingredient.id === lock.ingredientId).length === 1,
  )
}

export function ingredientLocksMatch(
  base: SimulationPublic,
  candidate: SimulationPublic,
  locks: readonly IngredientLock[],
): boolean {
  if (!validIngredientLocks(base, locks)) return false
  if (locks.length === 0) return true
  if (!Array.isArray(candidate?.ingredients)) return false
  return locks.every((lock) => {
    const original = base.ingredients.find((ingredient) => ingredient.id === lock.ingredientId)!
    const matches = candidate.ingredients.filter(
      (ingredient) => ingredient.id === lock.ingredientId,
    )
    return (
      matches.length === 1 && lock.fields.every((field) => matches[0]![field] === original[field])
    )
  })
}

/** simulations.internal key holding the owner's locks so a reopened editor stays locked (C37). */
export const SAVED_INGREDIENT_LOCKS_KEY = 'ingredientLocks'

/** Saved locks that still anchor exactly one ingredient of `result`; stale or malformed entries drop out. */
export function restorableIngredientLocks(
  result: SimulationPublic | null | undefined,
  saved: unknown,
): IngredientLock[] {
  const parsed = ingredientLocksSchema.safeParse(saved)
  if (!parsed.success || !Array.isArray(result?.ingredients)) return []
  return parsed.data.filter(
    (lock) =>
      result.ingredients.filter((ingredient) => ingredient.id === lock.ingredientId).length === 1,
  )
}

/** Optional `ingredientLocks` of GET /simulations/:id. Absent when nothing is locked (legacy-safe). */
export function savedIngredientLocksField(
  result: SimulationPublic | null | undefined,
  saved: unknown,
): { ingredientLocks?: IngredientLock[] } {
  const locks = restorableIngredientLocks(result, saved)
  return locks.length ? { ingredientLocks: locks } : {}
}

export const INGREDIENT_LOCK_COPY = {
  en: {
    title: 'Keep ingredient fields',
    name: 'Name',
    qty: 'Amount',
    unit: 'Unit',
    hint: 'Keep these exact values during AI editing. Amount locks do not fix formulation percentages.',
    conflict:
      'The AI proposal did not preserve your locked ingredient fields, so it was not applied. Your draft is unchanged.',
    invalid:
      'The ingredient locks no longer match this draft. Review the ingredients and lock choices before trying again.',
  },
  ko: {
    title: '재료 항목 잠금',
    name: '재료명',
    qty: '수량',
    unit: '단위',
    hint: 'AI 수정 중 선택한 값을 유지해요. 수량 잠금은 배합비 고정이 아니에요.',
    conflict: 'AI 제안이 잠근 재료 항목을 유지하지 못해 적용하지 않았어요. 기존 초안은 그대로예요.',
    invalid:
      '잠금 항목이 현재 초안과 맞지 않아요. 재료와 잠금 선택을 확인한 뒤 다시 요청해 주세요.',
  },
  ja: {
    title: '材料項目を固定',
    name: '材料名',
    qty: '数量',
    unit: '単位',
    hint: 'AI修正中は選んだ値を維持します。数量の固定は配合比の固定ではありません。',
    conflict:
      'AIの提案が固定した材料項目を維持しなかったため適用しませんでした。下書きは変わりません。',
    invalid: '固定項目が現在の下書きと一致しません。材料と固定項目を確認して再度依頼してください。',
  },
  zh: {
    title: '锁定食材字段',
    name: '食材名',
    qty: '数量',
    unit: '单位',
    hint: 'AI修改时保留所选的确切值。锁定数量并不固定配比。',
    conflict: 'AI方案未保留锁定的食材字段，因此未应用。原草稿保持不变。',
    invalid: '锁定字段与当前草稿不匹配。请检查食材和锁定选项后重试。',
  },
} as const

export function ingredientLockNotice(error: unknown, language: Language): string | null {
  const code = error && typeof error === 'object' && 'code' in error ? error.code : null
  return code === 'INGREDIENT_LOCK_CONFLICT'
    ? INGREDIENT_LOCK_COPY[language].conflict
    : code === 'INGREDIENT_LOCK_INVALID'
      ? INGREDIENT_LOCK_COPY[language].invalid
      : null
}
